import React from "react";
import PageHero from "../components/layout/PageHero";
import Reveal from "../components/Reveal";
import { Testimonial } from "../components/home/Process";
import Icon from "../components/Icon";
import { useContent } from "../context/ContentContext";
import { mediaUrl } from "../api";
import { CheckCircle2, Target, Compass } from "lucide-react";

export default function About() {
  const { content } = useContent();
  const images = content.images || {};
  const stats = content.stats || [];
  const whyChoose = content.whyChoose || [];
  const company = content.company || {};
  return (
    <>
      <PageHero
        eyebrow="About Novocreste"
        title="A global pharmaceutical sourcing partner"
        subtitle={`${company.legalName} connects healthcare companies worldwide with a trusted network of qualified manufacturing partners across every dosage form, category and regulated market.`}
      />

      {/* Split intro */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="rounded-[22px] overflow-hidden ring-1 ring-black/5 shadow-[0_30px_70px_-30px_rgba(12,97,82,0.4)]">
              <img src={mediaUrl(images.pills)} alt="Pharmaceutical products" className="w-full h-[440px] object-cover" loading="lazy" />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <h2 className="font-display text-[32px] lg:text-[40px] leading-[1.1] text-foreground">
                Sourcing made simple, faster and compliant
              </h2>
              <p className="mt-5 text-[16px] leading-relaxed text-muted-foreground">
                We connect global healthcare companies with a wide network of qualified manufacturers,
                helping buyers move faster with regulatory confidence, commercial clarity and reliable
                execution. From product brief to delivery, one dedicated team manages your entire sourcing journey.
              </p>
            </Reveal>
            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              <Reveal className="bg-card rounded-2xl border border-border p-6">
                <Target className="h-6 w-6 text-[#2ED9B8]" />
                <h3 className="mt-4 font-display text-[19px] text-foreground">Our Mission</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Bring quality medicines to patients worldwide by simplifying global sourcing.
                </p>
              </Reveal>
              <Reveal delay={90} className="bg-card rounded-2xl border border-border p-6">
                <Compass className="h-6 w-6 text-[#2ED9B8]" />
                <h3 className="mt-4 font-display text-[19px] text-foreground">Our Approach</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Pre-qualified partners, transparent pricing and end-to-end documentation support.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Stats band */}
      <section className="py-4">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="rounded-2xl brand-gradient grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10 overflow-hidden">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 70} className="p-8 text-center">
                <p className="font-display text-4xl lg:text-5xl text-white">{s.value}</p>
                <p className="mt-2 text-sm text-white/75">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Values / strengths */}
      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="max-w-2xl">
            <h2 className="font-display text-[32px] lg:text-[42px] leading-[1.1] text-foreground">
              What makes us a dependable partner
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {whyChoose.map((w, i) => (
              <Reveal key={w.title} delay={(i % 3) * 80}>
                <div className="h-full bg-card rounded-2xl border border-border p-7">
                  <div className="h-12 w-12 rounded-xl bg-[#0f2236] flex items-center justify-center">
                    <Icon name={w.icon} className="h-6 w-6 text-[#2ED9B8]" strokeWidth={1.8} />
                  </div>
                  <h3 className="mt-5 font-display text-[20px] text-foreground">{w.title}</h3>
                  <p className="mt-2.5 text-[14.5px] leading-relaxed text-muted-foreground">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Testimonial />
    </>
  );
}
